import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faClipboard,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import config from "../../../config";
import { getlist } from "../../../contexts/apicontexts";
import {
  formatDateTime,
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  getPriorityClasses,
  getStatusClasses,
  isCustomerIdentity,
  normalizeTicketData,
} from "../ticketUtils";

const canManageAllTickets = (identity) =>
  (identity?.normalizedRoles || []).some((role) => {
    return role.includes("admin") || role.includes("support") || role.includes("help") || role.includes("manager") || role.includes("hr");
  });

export default function AdminCreatedTicketsPage() {
  const navigate = useNavigate();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);
  const allowAccess = useMemo(() => !isCustomerIdentity(identity) && canManageAllTickets(identity), [identity]);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const fetchTickets = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getlist(`${config.baseurl}Ticket/getadmincreatedlist`);
      if (response?.status === 200) {
        setTickets(Array.isArray(response.data) ? response.data.map(normalizeTicketData) : []);
      } else {
        setError("Admin-created tickets could not be loaded.");
      }
    } catch {
      setError("Admin-created tickets could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowAccess) return;
    fetchTickets();
  }, [allowAccess, fetchTickets]);

  const filteredTickets = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const statusFiltered = statusFilter === "All" ? tickets : tickets.filter((ticket) => ticket.status === statusFilter);

    if (!query) return statusFiltered;

    return statusFiltered.filter((ticket) =>
      [
        ticket.ticketNumber,
        ticket.subject,
        ticket.status,
        ticket.category,
        ticket.priority,
        ticket.contactEmail,
        ticket.domainUrl,
        ticket.customerName,
        ticket.organizationName,
        ticket.requesterName,
        ticket.requesterEmail,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [searchTerm, statusFilter, tickets]);

  const counts = useMemo(() => {
    return {
      total: tickets.length,
      new: tickets.filter((item) => item.status === "New").length,
      inProgress: tickets.filter((item) => item.status === "In Progress").length,
      onHold: tickets.filter((item) => item.status === "On Hold").length,
      resolved: tickets.filter((item) => item.status === "Resolved").length,
    };
  }, [tickets]);

  if (!allowAccess) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 md:p-6">
        <Breadcrumb pageName="Admin Created Tickets" currentPage="Admin Created Tickets" />
        <div className="mt-4 rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
          This page is available only to support admins and managers.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="Admin Created Tickets" currentPage="Admin Created Tickets" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.20),_transparent_28%),linear-gradient(135deg,#0f172a,#1e293b_58%,#334155)] px-5 py-6 text-white md:px-7 md:py-8">
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
                <FontAwesomeIcon icon={faClipboard} />
                Admin Ticket History
              </div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Tickets opened by admins</h1>
              <p className="mt-3 text-sm text-slate-200 md:text-base">
                This list only shows support requests created from the admin site on behalf of customers.
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.fullName || "User"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identityRoleLabel}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.email || "No email found"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate("/tickets/admin/new-request")}
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-amber-300"
              >
                Open Support Request
              </button>
              <button
                type="button"
                onClick={fetchTickets}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 border-b border-slate-200 bg-slate-50/80 p-5 md:grid-cols-5 md:p-6">
          <StatCard label="Total" value={counts.total} />
          <StatCard label="New" value={counts.new} />
          <StatCard label="In Progress" value={counts.inProgress} />
          <StatCard label="On Hold" value={counts.onHold} />
          <StatCard label="Resolved" value={counts.resolved} />
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Admin-created ticket inbox</h2>
              <p className="mt-1 text-sm text-slate-500">
                Search and review only the tickets created by administrators on behalf of customers.
              </p>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 min-w-[160px]"
              >
                <option value="All">All Status</option>
                <option value="New">New</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="Resolved">Resolved</option>
              </select>

              <div className="relative w-full max-w-md">
                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search by subject, requester, customer, or ticket number"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-slate-400"
                />
              </div>
            </div>
          </div>

          {error ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center text-sm text-slate-500">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading admin-created tickets...
            </div>
          ) : filteredTickets.length ? (
            <div className="space-y-3">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/tickets/${ticket.id}`, { state: { backTo: "/tickets/admin-created" } })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(`/tickets/${ticket.id}`, { state: { backTo: "/tickets/admin-created" } });
                    }
                  }}
                  className={`w-full cursor-pointer rounded-2xl border p-4 text-left transition hover:border-slate-300 hover:shadow-md ${ticket.hasUnread ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"}`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                          {ticket.ticketNumber}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusClasses(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getPriorityClasses(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                          Admin created
                        </span>
                        {ticket.customerName ? (
                          <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700">
                            {ticket.customerName}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="truncate text-base font-semibold text-slate-900">{ticket.subject}</h3>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-500">
                        <span>{ticket.category}</span>
                        <span>{ticket.contactEmail}</span>
                        <span>{ticket.domainUrl || "No domain provided"}</span>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-slate-500 md:grid-cols-2">
                        <span>Requester: {ticket.requesterName || ticket.userName || "Not available"}</span>
                        <span>Requester ID: {ticket.requesterUserId || ticket.userId || "Not available"}</span>
                        {ticket.customerName ? <span>Customer: {ticket.customerName}</span> : null}
                        {ticket.organizationName ? <span>Organization: {ticket.organizationName}</span> : null}
                      </div>
                    </div>

                    <div className="shrink-0 text-right text-xs text-slate-500">
                      <div>Opened {formatDateTime(ticket.createdAt)}</div>
                      <div className="mt-1">Last update {formatDateTime(ticket.lastReplyAt || ticket.createdAt)}</div>
                      <div className="mt-3 flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/tickets/${ticket.id}`, { state: { backTo: "/tickets/admin-created" } });
                          }}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                        >
                          Open details
                          <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white">
                <FontAwesomeIcon icon={faTicket} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No admin-created tickets found</h3>
              <p className="mt-2 text-sm text-slate-500">
                Open a support request from the admin site to see it here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
